/**
 * Users Service
 * Uses unified API client for all HTTP requests
 * Provides user and agent-related API methods
 */

import apiClient from '../lib/api/client';
import { getApiUrl } from '../config/config';
import { showInfo } from '../utils/toast';

// Get users list (agents)
export const getUsersList = async () => {
  try {
    //console.log('🚀 Fetching users list...');
    const response = await apiClient.get(getApiUrl('USER', 'GET_USERS_LIST'));
    console.log('✅ Users list fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching users list:', error);
    throw error;
  }
};

// Snapshot pagination for get-users-list: the server pins the result set with a
// snapshot_id (valid for snapshot_expires_in_seconds) so paging forward doesn't
// shift as records change. Kept in memory only (passed in per call by the caller,
// carried forward from the previous response) - never persisted to localStorage.

// True when the server rejected the request because the pinned snapshot_id
// is no longer valid: { isSuccess: false, data: null, error: { code: 410, message: 'SNAPSHOT_EXPIRED' } }
const isSnapshotExpiredError = (error) => {
  const errorPayload = error.response?.data?.error;
  return error.response?.status === 410 || errorPayload?.code === 410 || errorPayload?.message === 'SNAPSHOT_EXPIRED';
};

// Get users list, paginated via page/page_size + snapshot_id (default page_size 5).
// snapshotId must be supplied by the caller (e.g. from the previous page's response
// meta.snapshot_id) - this function does not read or write any storage itself.
// If the server reports the snapshot has expired (410 SNAPSHOT_EXPIRED), the request
// is retried once without a snapshot_id.
export const getUsersListPaginated = async ({ page = 1, pageSize = 5, snapshotId, q } = {}) => {
  const buildParams = (sid) => {
    const params = { page, page_size: pageSize };
    if (sid) params.snapshot_id = sid;
    if (q) params.q = q;
    return params;
  };

  try {
    const response = await apiClient.get(getApiUrl('USER', 'GET_USERS_LIST'), {
      params: buildParams(snapshotId),
    });
    return response.data;
  } catch (error) {
    if (isSnapshotExpiredError(error) && snapshotId) {
      console.warn('⚠️ Users list snapshot expired, refetching without snapshot_id');
      showInfo('The user list refreshed after a short pause. Loading the latest results…');

      try {
        const retryResponse = await apiClient.get(getApiUrl('USER', 'GET_USERS_LIST'), {
          params: buildParams(null),
        });
        return retryResponse.data;
      } catch (retryError) {
        console.error('❌ Error re-fetching users list after snapshot expiry:', retryError);
        throw retryError;
      }
    }

    console.error('❌ Error fetching paginated users list:', error);
    throw error;
  }
};

// Get the agent list for the /chat Inbox and My tab filter dropdowns, paginated via
// page/page_size + snapshot_id (default page_size 5) with the same snapshot contract
// as getUsersListPaginated. Unlike get-users-list, each userlist entry carries an
// chat_count used to group agents into "assigned"/"no assigned chats" sections.
export const getAgentFilterList = async ({ page = 1, pageSize = 5, snapshotId, q, tab } = {}) => {
  const buildParams = (sid) => {
    const params = { page, page_size: pageSize };
    if (sid) params.snapshot_id = sid;
    if (q) params.q = q;
    if (tab) params.tab = tab;
    return params;
  };

  try {
    const response = await apiClient.get(getApiUrl('USER', 'GET_AGENT_FILTER_LIST'), {
      params: buildParams(snapshotId),
    });
    return response.data;
  } catch (error) {
    if (isSnapshotExpiredError(error) && snapshotId) {
      console.warn('⚠️ Agent filter list snapshot expired, refetching without snapshot_id');
      showInfo('The agent list refreshed after a short pause. Loading the latest results…');

      try {
        const retryResponse = await apiClient.get(getApiUrl('USER', 'GET_AGENT_FILTER_LIST'), {
          params: buildParams(null),
        });
        return retryResponse.data;
      } catch (retryError) {
        console.error('❌ Error re-fetching agent filter list after snapshot expiry:', retryError);
        throw retryError;
      }
    }

    console.error('❌ Error fetching agent filter list:', error);
    throw error;
  }
};

// Extract the flat user array from a get-users-list response,
// supporting both the old (array) and new ({ meta, userlist }) shapes
const extractUsersFromResponse = (data) => {
  if (Array.isArray(data)) {
    return data;
  }
  if (data && Array.isArray(data.userlist)) {
    return data.userlist;
  }
  return [];
};

// Get agents only (filtered from users list)
export const getAgentsList = async () => {
  try {
    const response = await getUsersList();
    if (response.isSuccess && response.data) {
      const users = extractUsersFromResponse(response.data);
      // Filter only agents and approved users
      const agents = users.filter(user =>
        user.role === 'agent' &&
        user.is_approved === true &&
        user.is_active === true
      );
      return {
        isSuccess: true,
        data: agents,
        error: null
      };
    }
    return response;
  } catch (error) {
    console.error('❌ Error fetching agents list:', error);
    throw error;
  }
};

// Get both agents and admins (filtered from users list)
export const getAgentsAndAdminsList = async () => {
  try {
    const response = await getUsersList();
    if (response.isSuccess && response.data) {
      const users = extractUsersFromResponse(response.data);
      // Filter agents and admins with approved and active status
      const agentsAndAdmins = users.filter(user =>
        (user.role === 'agent' || user.role === 'admin') &&
        user.is_approved === true &&
        user.is_active === true
      );
      return {
        isSuccess: true,
        data: agentsAndAdmins,
        error: null
      };
    }
    return response;
  } catch (error) {
    console.error('❌ Error fetching agents and admins list:', error);
    throw error;
  }
};

// Update user approval status
export const updateUserApproval = async (userId, isApproved) => {
  try {
    const updateUrl = `${getApiUrl('USER', 'UPDATE_USER_APPROVAL')}/${userId}/`;
    const response = await apiClient.patch(updateUrl, { is_approved: isApproved });
    return response.data;
  } catch (error) {
    console.error('❌ Error updating user approval:', error);
    throw error;
  }
};

// Update user role
export const updateUserRole = async (userId, role) => {
  try {
    console.log('✏️ Updating user role:', { userId, role });
    const updateUrl = `${getApiUrl('USER', 'UPDATE_USER')}/${userId}/`;
    const response = await apiClient.patch(updateUrl, { role });
    console.log('✅ User role updated successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error updating user role:', error);
    if (error.response) {
      console.error('❌ Response error details:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data
      });
    }
    throw error;
  }
};

// Update user profile (first/last name)
export const updateUserProfile = async (userId, payload) => {
  try {
    const updateUrl = `${getApiUrl('USER', 'UPDATE_USER')}/${userId}/`;
    const response = await apiClient.patch(updateUrl, payload);
    return response.data;
  } catch (error) {
    console.error('❌ Error updating user profile:', error);
    throw error;
  }
};

// Upload user profile picture
export const uploadUserProfilePicture = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post(getApiUrl('USER', 'PROFILE_PIC'), formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (error) {
    console.error('❌ Error uploading user profile picture:', error);
    throw error;
  }
};

// Delete user profile picture
export const deleteUserProfilePicture = async () => {
  try {
    const response = await apiClient.delete(getApiUrl('USER', 'PROFILE_PIC'));
    return response.data;
  } catch (error) {
    console.error('❌ Error deleting user profile picture:', error);
    throw error;
  }
};

// Get the current agent's availability status
export const getAgentAvailabilityStatus = async () => {
  try {
    const response = await apiClient.get(getApiUrl('USER', 'AGENT_AVAILABILITY_STATUS'));
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching agent availability status:', error);
    throw error;
  }
};

// Update the current agent's availability status
export const updateAgentAvailabilityStatus = async (availabilityStatus) => {
  try {
    const response = await apiClient.patch(getApiUrl('USER', 'AGENT_AVAILABILITY_STATUS'), {
      availability_status: availabilityStatus,
    });
    return response.data;
  } catch (error) {
    console.error('❌ Error updating agent availability status:', error);
    throw error;
  }
};

const usersService = {
  getUsersList,
  getUsersListPaginated,
  getAgentFilterList,
  getAgentsList,
  getAgentsAndAdminsList,
  updateUserRole,
  updateUserApproval,
  updateUserProfile,
  uploadUserProfilePicture,
  deleteUserProfilePicture,
  getAgentAvailabilityStatus,
  updateAgentAvailabilityStatus,
};

export default usersService;
